import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { claimWindow, ERP_WINDOW, linkApp, openPos } from "../lib/windows";
import { useLang } from "../i18n";
import { useSession } from "../lib/session";
import { Icon } from "./Icon";
import type { IconName } from "./icons";

// Navigation order, labels and routes from the legacy audit, as in the approved prototype (app.js NAV), without the
// HR group: HR is outside Delta's scope (see isExcludedRoute in @delta/shared).
interface NavTop { k: string; ic: IconName; route?: string; sub?: [string, string][] }
export const NAV: NavTop[] = [
  { k: "home", ic: "home", route: "/home" },
  { k: "sales", ic: "cart", sub: [["salesInvoices", "/invoices/Sale"], ["quotations", "/invoices/Quotation"], ["salesRefunded", "/invoices/SaleRefund"], ["clients", "/Customer"], ["collects", "/receipts/Collect"], ["pcTitle", "/price-controls"]] },
  { k: "purchases", ic: "bag", sub: [["purchasesInvoices", "/invoices/Purchase"], ["purchasesRefund", "/invoices/PurchaseRefund"], ["suppliers", "/Supplier"], ["payments", "/receipts/Payment"]] },
  { k: "generalAccounts", ic: "ledger", sub: [["journalEntries", "/journals"], ["cashTransfers", "/transfers"], ["deposit", "/receipts/Deposit"], ["withdraw", "/receipts/Withdraw"], ["partners", "/partners"], ["openingBalances", "/opening-balances"], ["legacyHistory", "/legacy-history"]] },
  { k: "items", ic: "box", sub: [["allItems", "/items/allItems"], ["categories", "/item-Category"], ["unitConfig", "/unitsConfig"], ["barcodePrint", "/items/qrPrint"], ["itemKinds", "/item-kinds"]] },
  { k: "expenses", ic: "receipt", route: "/Expense" },
  { k: "reports", ic: "chart", route: "/reports" },
  { k: "fixedAssets", ic: "building", sub: [["allAssets", "/assets/all"], ["assetTypes", "/assets/types"], ["assetPurchases", "/assets/purchases"], ["assetDepreciation", "/assets/depreciation"]] },
  { k: "warehouses", ic: "warehouse", sub: [["allWarehouses", "/warehouses/all"], ["whInventory", "/invoices/Inventory"], ["beginningInventory", "/invoices/BeginningInventory"], ["corruptedInvoices", "/invoices/Corrupted"], ["whTransfers", "/invoices/Transfer"], ["stockOnHand", "/warehouse-stock"]] },
  { k: "settings", ic: "settings", sub: [["setOrg", "/settings/organization"], ["setPeriodLock", "/settings/period-lock"], ["setPosButtons", "/settings/pos-payment-buttons"], ["setPosting", "/settings/posting"], ["setChartTemplate", "/settings/chart-template"], ["setBranches", "/settings/branches"], ["setTreasury", "/settings/treasury"], ["accountsIndex", "/settings/accountIndex"], ["indexesTypes", "/settings/accountTypes"], ["costCenters", "/cost-centers"], ["taxSettings", "/settings/tax"], ["expenseTypes", "/settings/expense-types"], ["tobSettings", "/settings/tobacco"], ["printers", "/printers"], ["users", "/users-settings"], ["setPackage", "/settings/package"], ["clientArea", "/client-area"]] },
  { k: "pos", ic: "pos", sub: [["posHome", "/pos"], ["posSale", "/pos/sale"], ["posRefund", "/pos/refund"], ["posInvoices", "/pos/invoices"], ["posReports", "/pos/reports"]] }
];

export function findNav(path: string): { top: NavTop; sub?: string } | null {
  for (const n of NAV) {
    if (n.route === path) return { top: n };
    for (const [k, r] of n.sub ?? []) if (r === path) return { top: n, sub: k };
  }
  return null;
}

/** The Delta logo, linking home: the ERP's Home, or the shell's own home (POS Home in the register) via `to`. */
export function Wordmark({ light, to = "/home", label = "Delta" }: { light: boolean; to?: string; label?: string }) {
  const { lang } = useLang();
  return (
    <Link className="wordmark" to={to} aria-label={label} data-testid="wordmark">
      <img className="mark" src="/delta-mark.svg" alt="" />
      <span><span className="wm-text" style={{ color: light ? "#fff" : "var(--navy-900)" }}>{lang === "ar" ? "دلتا" : "DELTA"}</span></span>
    </Link>
  );
}

const SB_SCROLL = "delta.sidebar.scroll", SB_HIDDEN = "delta.sidebar.hidden";
/** Browser storage can be missing or refuse (private windows, blocked site data): the menu then simply starts at the top. */
function store<T>(f: () => T): T | null { try { return f(); } catch { return null; } }

function Sidebar({ navKey, hidden, onHide }: { navKey: string; hidden: boolean; onHide(): void }) {
  const { t } = useLang();
  const { missingFor, me, closed } = useSession();
  const hit = findNav(navKey);
  // A single-open accordion (owner's request): at most one group is expanded. Opening a group closes the other, a
  // click on the open group closes it, and arriving on a page (link, typed address, refresh) expands only its group.
  const [open, setOpen] = useState<string | null>(() => (hit?.top.sub ? hit.top.k : null));
  useEffect(() => { if (hit?.top.sub) setOpen(hit.top.k); }, [hit?.top.k, hit?.top.sub]);
  // Settings › Posting is in the menu for those who post or manage posting; others still see the waiting state on
  // each document, and the page itself (read only) by its address.
  // Settings › Chart template is for those who see the chart or the organization's settings (the server checks the same).
  const chartKeys = ["setting", "chartOfAccountLevel2s", "chartOfAccountLevel3s", "r1chartOfAccountsTree"];
  const ok = (r: string) => (closed ? r === "/settings/package" : true) && !missingFor(r).length && (r !== "/settings/posting" || !!me?.posting.manage || !!me?.posting.post.length)
    && (r !== "/settings/chart-template" || !!me?.user.administrator || chartKeys.some((k) => me?.permissions.includes(k)))
    // Sales › Price controls (migration 039): for whoever sells; approvers decide there, the owner grants there.
    && (r !== "/price-controls" || !!me?.user.administrator || !!me?.permissions.includes("invoiceSales"))
    // Settings › Package (migration 043): only businesses on packages (signed up), for the client-area key.
    && (r !== "/settings/package" || !!me?.package)
    // General accounts › Legacy history: only in a business that holds migrated legacy documents.
    && (r !== "/legacy-history" || !!me?.organization.legacyHistory);
  // The menu keeps its place (correction pass of 29 Oct 2026, item 6). Every page mounts its own Shell, so the scroll
  // offset is kept for the tab in sessionStorage and put back before the first paint: choosing a bottom Settings item,
  // Back, Forward or a refresh leave the menu where it was. The chosen item is then brought into view only when it is
  // not (a new tab arriving by address, a smaller window), and never moves a menu that already shows it.
  const scroller = useRef<HTMLElement>(null);
  // Showing a hidden menu again (item 7) puts it back the same way: the open group never changed, the offset is reread.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || hidden) return;
    el.scrollTop = Number(store(() => sessionStorage.getItem(SB_SCROLL)) ?? 0) || 0;
    const a = el.querySelector<HTMLElement>(".nav-sub a.active, a.nav-item.active");
    if (a) {
      const box = el.getBoundingClientRect(), r = a.getBoundingClientRect();
      if (r.top < box.top || r.bottom > box.bottom) el.scrollTop += r.top < box.top ? r.top - box.top - 8 : r.bottom - box.bottom + 8;
    }
  }, [hidden]);
  const remember = () => { const el = scroller.current; if (el) store(() => sessionStorage.setItem(SB_SCROLL, String(Math.round(el.scrollTop)))); };
  return (
    <aside className="sidebar" id="app-sidebar" aria-label={t("menu")}>
      <div className="sb-inner">
        <div className="sb-head"><Wordmark light />
          <button type="button" className="sb-collapse" id="sb-hide" onClick={onHide} aria-controls="app-sidebar" aria-expanded="true" aria-label={t("sbHide")} title={t("sbHide")} data-testid="sb-hide"><Icon name="back" className="flip" /></button>
        </div>
        <nav className="sb-scroll" ref={scroller} onScroll={remember}>
          {NAV.map((n) => {
            if (n.route) {
              if (!ok(n.route)) return null;
              return <NavLink key={n.k} className={`nav-item ${hit?.top === n ? "active" : ""}`} to={n.route} title={t(n.k)}><Icon name={n.ic} /><span className="lbl">{t(n.k)}</span></NavLink>;
            }
            const subs = (n.sub ?? []).filter(([, r]) => ok(r));
            if (!subs.length) return null;
            const isOpen = open === n.k;
            return (
              <div key={n.k} className={`nav-group ${isOpen ? "open" : ""} ${hit?.top === n ? "has-active" : ""}`}>
                <button className="nav-item" aria-expanded={isOpen} title={t(n.k)} onClick={() => setOpen((k) => (k === n.k ? null : n.k))}>
                  <Icon name={n.ic} /><span className="lbl">{t(n.k)}</span><Icon name="chev" className="chev" />
                </button>
                <div className="nav-sub">
                  {subs.map(([k, r]) => r.startsWith("/pos")
                    // The register opens in its own window (or brings the open one to the front).
                    ? <a key={k} href={r} onClick={(e) => { if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) { e.preventDefault(); openPos(r); } }} data-testid={`nav-${k}`}>{t(k)}<Icon name="copy" className="nav-ext" /></a>
                    : <Link key={k} to={r} className={hit?.sub === k ? "active" : ""}>{t(k)}</Link>)}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="sb-foot"><div className="sb-plan"><span className="dot" /><div><b>{t("planLabel")}</b>{t("planSub")}</div></div></div>
      </div>
    </aside>
  );
}

function scopeNote(t: ReturnType<typeof useLang>["t"], L: ReturnType<typeof useLang>["L"], s: ReturnType<typeof useSession>, lang: string): string {
  const me = s.me!;
  if (me.user.administrator) return t("scopeAll");
  const sep = lang === "ar" ? "، " : ", ";
  return t("scopeOf", {
    b: L(s.branches.find((b) => b.id === me.scope.branchId)),
    w: s.warehouses.map((x) => L(x)).join(sep) || "–",
    tr: s.treasuries.map((x) => L(x)).join(sep) || "–"
  });
}

function Topbar({ onMenu, drawer, hidden, onShow }: { onMenu(): void; drawer: boolean; hidden: boolean; onShow(): void }) {
  const { t, L, lang, setLang } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  const me = s.me!;
  const initials = lang === "ar" ? me.user.name.slice(0, 1) : me.user.name.split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();
  return (
    <header className="topbar">
      <button className="btn btn-ghost btn-icon menu-btn" id="sb-menu" onClick={onMenu} aria-label={t("menu")} aria-controls="app-sidebar" aria-expanded={drawer}><Icon name="menu" /></button>
      {hidden && <button className="btn btn-ghost btn-icon sb-show" id="sb-show" onClick={onShow} aria-label={t("sbShow")} title={t("sbShow")} aria-controls="app-sidebar" aria-expanded="false" data-testid="sb-show"><Icon name="menu" /></button>}
      <div className="tb-search input-group"><span className="lead"><Icon name="search" /></span><input className="control" type="search" placeholder={t("search")} aria-label={t("search")} /></div>
      <div className="tb-right">
        <label className="branch-select"><Icon name="store" /><span className="bs-label">{t("branch")}</span>
          <select className="control" aria-label={t("branch")} value={s.branch} onChange={(e) => s.setBranch(e.target.value)} data-testid="branch-select">
            {me.user.administrator && <option value="all">{t("allBranches")}</option>}
            {s.branches.map((b) => <option key={b.id} value={b.id}>{L(b)}</option>)}
          </select>
        </label>
        <span className="demo-badge" title={t("environmentTag")}><Icon name="flask" /><span>{t("environmentTag")}</span></span>
        <button className="btn btn-ghost lang-btn" onClick={() => setLang(lang === "ar" ? "en" : "ar")} lang={lang === "ar" ? "en" : "ar"}><Icon name="globe" />{t("switchLang")}</button>
        <button className="btn btn-ghost btn-icon" aria-label={t("notifications")}><Icon name="bell" /></button>
        <div style={{ position: "relative" }} ref={ref}>
          <button className="user-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)} data-testid="user-menu">
            <span className="avatar">{initials}</span>
            <span className="u-meta"><div className="u-name">{me.user.name}</div><div className="u-role">{me.user.administrator ? t("administrator") : t("userKind")}</div></span>
          </button>
          <div className={`menu-pop ${menu ? "open" : ""}`} role="menu">
            <div className="um-scope"><b>{t("userScopeNow")}</b><span>{L(me.organization)} · {scopeNote(t, L, s, lang)}</span></div>
            <button role="menuitem" onClick={async () => { setMenu(false); await s.signOut(); nav("/login"); }}><Icon name="logout" className="flip" />{t("signOut")}</button>
          </div>
        </div>
      </div>
    </header>
  );
}

/** The application frame: sidebar, top bar and page content. */
export function Shell({ navKey, children, bar }: { navKey: string; children: ReactNode; bar?: ReactNode }) {
  const [drawer, setDrawer] = useState(false);
  // The menu can be hidden on a wide screen (correction pass of 29 Oct 2026, item 7): "Hide menu" in its head, "Show
  // menu" in the top bar. The choice is kept in localStorage, so it holds across pages and reloads. Narrower windows
  // keep the icon rail and the drawer, where the same button closes the drawer. Focus follows to the other button.
  const [hidden, setHidden] = useState(() => store(() => localStorage.getItem(SB_HIDDEN)) === "1");
  const focusTo = useRef<string | null>(null);
  useEffect(() => { if (focusTo.current) { document.getElementById(focusTo.current)?.focus(); focusTo.current = null; } }, [hidden, drawer]);
  const hide = (h: boolean) => { focusTo.current = h ? "sb-show" : "sb-hide"; setHidden(h); store(() => localStorage.setItem(SB_HIDDEN, h ? "1" : "0")); };
  const loc = useLocation();
  const go = useNavigate();
  useEffect(() => setDrawer(false), [loc.pathname]);
  // Escape closes the drawer and gives the focus back to the menu button.
  useEffect(() => {
    if (!drawer) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { focusTo.current = "sb-menu"; setDrawer(false); } };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [drawer]);
  // This window is the ERP's: the register's "Back to dashboard" finds it by name instead of opening another.
  useEffect(() => claimWindow(ERP_WINDOW, (p) => go(p)), [go]);
  useEffect(() => linkApp("erp"), []);
  return (
    <div className={`shell ${drawer ? "drawer-open" : ""} ${hidden ? "sb-hidden" : ""}`}>
      <Sidebar navKey={navKey} hidden={hidden && !drawer} onHide={() => { if (drawer) { focusTo.current = "sb-menu"; setDrawer(false); } else hide(true); }} />
      <div className="scrim" onClick={() => setDrawer(false)} />
      <div className="main">
        <Topbar onMenu={() => { if (!drawer) focusTo.current = "sb-hide"; setDrawer((d) => !d); }} drawer={drawer} hidden={hidden} onShow={() => hide(false)} />
        <main className="content" id="content"><PackageNotice />{children}</main>
        {bar}
      </div>
    </div>
  );
}

/**
 * The package warning (migration 043): before the current trial or subscription ends with nothing to follow it, and
 * after it has ended. Only the owner and administrators see it, never on the register (the POS screens have no Shell).
 */
function PackageNotice() {
  const { t } = useLang();
  const { me } = useSession();
  const loc = useLocation();
  const p = me?.package;
  if (!p || !(me.user.owner || me.user.administrator)) return null;
  // The package screen says the same itself when the business is closed.
  if (p.access !== "open" && loc.pathname === "/settings/package") return null;
  const expired = p.state === "expired" || p.access !== "open";
  if (!expired && !p.expiringSoon) return null;
  return (
    <div className={`pkg-notice ${expired ? "bad" : "warn"}`} role="status" data-testid="pkg-notice">
      <Icon name="alert" />
      <span>{p.access !== "open" ? t(p.access === "none" ? "pkgClosedNoneBody" : "pkgClosedEndedBody") : expired ? t("pkgNoticeExpired") : t(p.kind === "trial" ? "pkgNoticeTrial" : "pkgNoticePaid", { d: p.endsOn ?? "", n: p.daysLeft ?? 0 })}</span>
      <Link to="/settings/package">{t("pkgNoticeOpen")}</Link>
    </div>
  );
}

export function Crumbs({ list }: { list: [string, string?][] }) {
  return (
    <nav className="crumbs" aria-label="breadcrumb">
      {list.map(([label, href], i) => (
        <span key={i} style={{ display: "contents" }}>
          {i > 0 && <Icon name="chevR" className="flip" />}
          {href ? <Link to={href}>{label}</Link> : <span>{label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function PageHead({ crumbs, title, badge, sub, actions }: { crumbs: [string, string?][]; title: string; badge?: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  useEffect(() => { document.title = `${title} · Delta`; }, [title]);
  return (
    <div className="page-head">
      <div>
        <Crumbs list={crumbs} />
        <div className="doc-no"><h1>{title}</h1>{badge}</div>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}
